<template>
  <esg-base-dashboard-card :title="product.name"
                           :subtitle="productFamily.name + ' 제품군'">
    <template #actions>
      <b-form>
        <b-form-row>
          <b-col>
            <label class="label"
                   for="year">연도</label>
            <b-form-select id="year"
                           size="sm"
                           v-model="year">
              <option v-for="year in yearOptions"
                      :key="year"
                      :value="year">{{ year }}
              </option>
            </b-form-select>
          </b-col>
        </b-form-row>
      </b-form>
    </template>

    <template #default>
      <!-- Stacked bar chart -->
      <b-container fluid
                   class="mt-2">
        <b class="content-title">전과정 온실가스 배출비율</b>
        <esg-base-chart>
          <loading :active="loading"
                   :z-index="1000"
                   :is-full-page="false"/>
          <product-lca-by-category :product-family="productFamily"
                                   :product="product"
                                   :year="year"
                                   v-show="!loading"
                                   @noData="$emit('noData')"
                                   @categorySelected="onCategorySelected"/>
        </esg-base-chart>
        <b class="annotation">세부정보를 보시려면 그래프의 온실가스 배출 비율을 클릭해주세요</b>
      </b-container>
      <!-- /Stacked bar chart -->

      <!-- LCA process detail -->
      <b-collapse v-model="visible"
                  class="mt-5">
        <LcaRawMaterial :product="product"
                        :year="year"
                        v-show="category === categories[0]"/>
        <LcaUpstreamTransportation :product="product"
                                   :year="year"
                                   v-show="category === categories[1]"/>
        <LcaProduction :product="product"
                       :year="year"
                       v-show="category === categories[2]"/>
        <LcaDownstreamTransportation :product="product"
                                     :year="year"
                                     v-show="category === categories[3]"/>
        <LcaDisposal :product="product"
                     :year="year"
                     v-show="category === categories[5]"/>
      </b-collapse>
      <!-- /LCA process detail -->
    </template>
  </esg-base-dashboard-card>
</template>
<script>
import EsgBaseDashboardCard from "@/components/esg/base/EsgBaseDashboardCard.vue";
import LcaRawMaterial from "@/components/esg/3. Monitoring/dashboardCards/LcaRawMaterial.vue";
import LcaUpstreamTransportation from "@/components/esg/3. Monitoring/dashboardCards/LcaUpstreamTransportation.vue";
import LcaProduction from "@/components/esg/3. Monitoring/dashboardCards/LcaProduction.vue";
import LcaDownstreamTransportation from "@/components/esg/3. Monitoring/dashboardCards/LcaDownstreamTransportation.vue";
import LcaDisposal from "@/components/esg/3. Monitoring/dashboardCards/LcaDisposal.vue";
import ProductLcaByCategory from "@/components/esg/3. Monitoring/charts2/ProductLcaByCategory.vue";
import EsgBaseChart from "@/components/esg/base/EsgBaseChart.vue";

export default {
  props: ['productFamily', 'product'],
  components: {
    EsgBaseChart,
    ProductLcaByCategory,
    EsgBaseDashboardCard,
    LcaRawMaterial,
    LcaUpstreamTransportation,
    LcaProduction,
    LcaDownstreamTransportation,
    LcaDisposal,
  },
  computed: {
    lca() {
      return this.$store.getters['ghg/lifeCycleStages'];
    }
  },
  watch: {
    product: {
      immediate: true,
      deep: true,
      handler() {
        this.visible = false;
        this.initialize();
      }
    },
    year: {
      immediate: true,
      deep: true,
      handler() {
        this.visible = false;
      }
    }
  },
  data() {
    return {
      loading: true,
      year: 0,
      yearOptions: [],
      visible: false,
      category: null,
      categories: this.$store.getters['ghg/lifeCycleStages']
    }
  },
  methods: {
    async initialize() {
      if (!this.product.productFamilyCode || !this.product.productCode) return

      try {
        this.loading = true

        let args = [{
          productFamilies: [this.product.productFamilyCode],
          products: [this.product.productCode],
        }];

        let data = new FormData();
        data.append('path', 'esg/lca/ghg_emission/main');
        data.append('json', JSON.stringify(args));

        let response = await axios.post('esg/lca/run/python/chart-data', data, {
          headers: {Authorization: accessToken()}
        });

        // If there is no data, throw an error
        if (!response.data.data) {
          this.$emit('noData')
          return
        }

        // Parse JSON data
        let list = JSON.parse(response.data.data)[0].data;
        this.yearOptions = [...new Set(list.map(row => row.year))];
        this.yearOptions.reverse();
        this.year = this.yearOptions[0];
      } catch (error) {
        this.loading = false
        await this.showError(error)
      } finally {
        this.loading = false
        this.refreshChart()
      }
    },
    onCategorySelected(categorySelected) {
      if (!this.category) {
        this.visible = true;
        this.category = categorySelected;
      } else if (this.category === categorySelected) {
        this.visible = false
        this.category = null
      } else {
        this.visible = true
        this.category = categorySelected
      }
    }
  }
}
</script>

<style scoped>
>>> {
  font-family: NanumGothic;
}

>>> .title {
  display: block;
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: .625rem;
}

>>> .subtitle {
  display: block;
  color: #888;
}

>>> .content-title {
  font-size: 1rem;
  display: block;
}

.annotation {
  font-size: 10px;
  font-weight: 500;
  font-family: NanumGothic;
  color: #888;
}

.custom-select {
  padding: 0.3rem 2.5rem 0.3rem .8rem;
  height: 2rem;
  border-radius: 10px;
  border: 1px solid #E7E7EE;
  background-color: #F6F6FA;
  background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M15.834 7.5L10.0007 13.3333L4.16732 7.5" stroke="%232E2E38" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>');
  background-position: calc(100% - 8px) 5px;
  background-size: 20px 20px;
  background-repeat: no-repeat;
  font-family: 'NanumGothic';
  color: #2E2E38;
  font-size: .875rem;
  font-weight: 400;
}

.custom-select:focus {
  border-color: unset;
  box-shadow: unset;
}

.form-control[readonly] {
  background-color: #F6F6FA !important;
}

.form-control:focus {
  box-shadow: none;
  border-color: unset;
}
</style>